import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  GenSearch,
  RefreshButton,
} from '@pinnacleautomotive/clarity-components';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getAllOrders } from '../../redux/actions/workQueue/allOrdersActions';
import { useGetVendorsQuery } from '../../redux/rtk/vendors.services';
import {
  currentUserCompanyId,
  selectUserDepartments,
} from '../../redux/selectors/userDataSelectors';
import {
  selectAllOrdersTotalWO,
  selectIsLoading,
} from '../../redux/selectors/workQueue/allOrdersSelectors';
import FilterWoCount from '../orders/allOrders/FilterWoCount';
import MultiSelectFilter from '../orders/allOrders/MultiSelectFilter';
import AllOrders from './allOrders';

const WorkQueue = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['orders', 'global']);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const userCompany = useSelector(currentUserCompanyId);
  const { data: vendors } = useGetVendorsQuery(userCompany);
  const departments = useSelector(selectUserDepartments);
  const totalWorkOrders = useSelector(selectAllOrdersTotalWO);
  const isLoading = useSelector(selectIsLoading);

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.up('sm'));

  const commonSx = {
    width: (smallScreen && '220px') || '100%',
    flexGrow: 1,
    bgcolor: theme.palette.background.paper,
    '& fieldset': {
      border: '1px solid',
      borderColor: theme.palette.divider,
    },
  };

  const onChangeVendor = e => {
    setSelectedVendor(e);
  };

  const onFilter = ({ selectedDepartments: newDepartments }) => {
    setSelectedDepartments(newDepartments);
  };

  return (
    <Grid container flexDirection="column">
      <Grid item>
        <Grid
          container
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          py={2}
          px={3}
        >
          <Grid item>
            <Typography variant="h6">
              {t('orders:workQueueSection.title')}
            </Typography>
            <Typography variant="body1">
              {t('orders:workQueueSection.subTitle')}
            </Typography>
          </Grid>
          <Grid item>
            <RefreshButton
              onClick={() =>
                dispatch(
                  getAllOrders({
                    companyId: userCompany,
                    pageNumber: 1,
                    searchQuery,
                    selectedVendor,
                    selectedDepartments,
                  })
                )
              }
            />
          </Grid>
        </Grid>
        <Grid
          container
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          px={3}
        >
          <Stack direction="row" gap={{ xs: 1, sm: 2 }} flexWrap={'wrap'}>
            <GenSearch
              placeholder={
                <Trans
                  t={t}
                  i18nKey="orders:workQSearch"
                  components={{ 1: <br /> }}
                />
              }
              searchTerm={filter => setSearchQuery(filter)}
              sx={{
                order: 0,
                width: '100%',
                [theme.breakpoints.up('lg')]: {
                  width: '336px',
                },
                '& form': { margin: '0px !important', height: '100%' },
              }}
            />
            <Stack sx={commonSx}>
              <MultiSelectFilter
                currentFilters={{ selectedDepartments }}
                inputLabel={t('orders:newOrder.services.department')}
                itemFilter={'selectedDepartments'}
                menuItemGetter={department => (
                  <MenuItem
                    key={department.departmentId}
                    value={department.departmentId}
                  >
                    {department.departmentName}
                  </MenuItem>
                )}
                onFilter={onFilter}
                options={departments}
                sufix={'departments'}
              />
            </Stack>
            <FormControl size="small" sx={commonSx}>
              <InputLabel id="vendor-label">Vendor</InputLabel>
              <Select
                labelId="vendor-label"
                label="Vendor"
                value={selectedVendor}
                onChange={event => onChangeVendor(event.target.value)}
                data-testid="vendorSelect"
              >
                <MenuItem value="all">
                  {t('orders:vendorsFilter.mainOption')}
                </MenuItem>
                {vendors?.map(vendor => (
                  <MenuItem
                    key={vendor.companyId}
                    value={vendor.companyId}
                    data-testid="selectOption"
                  >
                    {vendor.rootCompany.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Box sx={{ marginTop: { xs: 1, lg: 0 } }}>
            <FilterWoCount
              totalWorkOrders={totalWorkOrders}
              isLoading={isLoading}
            />
          </Box>
        </Grid>
        <Grid container px={3} py={1} flexDirection="column">
          <AllOrders
            companyId={userCompany}
            searchQuery={searchQuery}
            selectedVendor={selectedVendor}
            selectedDepartments={selectedDepartments}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default WorkQueue;
